import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  InputBase,
  Button,
  Avatar,
  Box,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Menu,
  MenuItem,
  CssBaseline,
  Stack,
  Container,
  alpha,
  TableContainer,
  Chip,
  TextField,
  InputAdornment,
  capitalize,
  Autocomplete,
  FormControl,
  Select,
  Collapse,
  ListItemIcon,
  ListItemText,
  Divider,
  } from '@mui/material';
import { Search as SearchIcon, Notifications as NotificationsIcon, ArrowDropDown, RemoveRedEyeOutlined, Add, FilterList, Clear, Assessment, GetApp, PictureAsPdf, TableChart, TrendingUp, AccountBalance, Schedule, Warning, AccountBalanceWallet, Inventory2Outlined } from '@mui/icons-material';
import AccessDeniedBox from '../../../components/AccessDeniedBox';
import AppNavbar from '../../../components/AppNavbar';
import Header from '../../../components/Header';
import SideMenu from '../../../components/SideMenu';
import EmptyState from '../../../components/EmptyState';
import Can from '../../../components/Can';
import usePermissions from '../../../hooks/usePermissions';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../../dashboard/theme/customizations';
import AppTheme from '../../../shared-theme/AppTheme';
import { StyledCellHeader } from '../../../components/StyledCellHeader';
import StatusChip from '../../../components/StatusChip';
import { useNavigate } from 'react-router-dom';
import Solicitudes from './solicitudes/solicitudes';
import { formatNumber } from '../../../utils/generalFunctions';
import Iniciales from './iniciales/iniciales';
import IrrecuperablesLoans from './irrecuperables';
import Garantias from './garantias';



const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const LoansManagement = (props) => {
  const [tabValue, setTabValue] = React.useState(0);
  const navigate = useNavigate();
  const [loanData, setLoanData] = useState([]);
  const [loanDataFiltered, setLoanDataFiltered] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    estado: '',
    sucursal: '',
    producto: '',
    montoMin: '',
    montoMax: '',
    fechaDesde: '',
    fechaHasta: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [sucursales, setSucursales] = useState([]);
  const [productos, setProductos] = useState([]);
  const [accessDenied, setAccessDenied] = useState(false);
  const [reportsMenuAnchor, setReportsMenuAnchor] = useState(null);
  const { hasPermission } = usePermissions();
  const puedeCrearPrestamo = hasPermission('prestamos', 'crear');
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleReportsMenuOpen = (event) => {
    setReportsMenuAnchor(event.currentTarget);
  };

  const handleReportsMenuClose = () => {
    setReportsMenuAnchor(null);
  };

  const handleReportSelect = (reportType) => {
    handleReportsMenuClose();
    // Handle different report types
    switch (reportType) {
      case 'balancedeprestamos':
        navigate('/gestion_comercial/prestamos/reportes/balancedeprestamos');
        break;
      case 'interesescobradosperiodo':
        navigate('/gestion_comercial/prestamos/reportes/interesescobradosperiodo');
        break;
      case 'antiguedadsaldo':
        navigate('/gestion_comercial/prestamos/reportes/antiguedadsaldo');
        break;
      case 'cuadredecajaprestamos':
        navigate('/gestion_comercial/prestamos/reportes/cuadredecajaprestamos');
        break;
      case 'historialproducto':
        navigate('/gestion_comercial/prestamos/reportes/historialproducto');
        break;
      default:
        console.log('Unknown report type:', reportType);
    }
  };


  const fetchLoans = React.useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/prestamos/all/${localStorage.getItem('idcompany')}`,{
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        }
      });

      if (response.status === 403) {
        setAccessDenied(true);
        return;
      }

      const data = await response.json();

      if (data && data.error === 'Acceso denegado') {
        setAccessDenied(true);
        return;
      }

      const filteredData = (Array.isArray(data) ? data : []).filter(loan => loan.producto != "");
      setLoanData(filteredData);
      setLoanDataFiltered(filteredData);
      
      // Extract unique sucursales and productos for filter
      const uniqueSucursales = [...new Set(filteredData.map(item => item.sucursal_nombre).filter(Boolean))];
      const uniqueProductos = [...new Set(filteredData.map(item => item.producto_financiero).filter(Boolean))];
      setSucursales(uniqueSucursales);
      setProductos(uniqueProductos);
    } catch (err) {
      console.error('Error fetching loans:', err);
    }
  }, []);

  useEffect(() => {
    fetchLoans();
  }, [fetchLoans]);

  const handleInputChange = (val) => {
    if(val != null) {
      setLoanDataFiltered(loanData.filter(loan => loan.loan_id==val.loan_id));
    } else {
      setLoanDataFiltered(loanData);
    }
  }

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  }

  const applyFilters = () => {
    let filtered = [...loanData];

    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(loan => 
        loan.loan_id?.toLowerCase().includes(searchTerm) ||
        loan.cliente_nombre?.toLowerCase().includes(searchTerm) ||
        loan.producto_financiero?.toLowerCase().includes(searchTerm) ||
        loan.sucursal_nombre?.toLowerCase().includes(searchTerm)
      );
    }

    // Estado filter
    if (filters.estado) {
      filtered = filtered.filter(loan => loan.estado_prestamo === filters.estado);
    }

    // Sucursal filter
    if (filters.sucursal) {
      filtered = filtered.filter(loan => loan.sucursal_nombre === filters.sucursal);
    }

    // Producto filter
    if (filters.producto) {
      filtered = filtered.filter(loan => loan.producto_financiero === filters.producto);
    }

    // Monto filters
    if (filters.montoMin) {
      filtered = filtered.filter(loan => parseFloat(loan.monto_aprobado) >= parseFloat(filters.montoMin));
    }
    if (filters.montoMax) {
      filtered = filtered.filter(loan => parseFloat(loan.monto_aprobado) <= parseFloat(filters.montoMax));
    }

    // Date filters
    if (filters.fechaDesde) {
      filtered = filtered.filter(loan => {
        const fechaLoan = new Date(loan.fecha_de_creacion.split('/').reverse().join('-'));
        const fechaDesde = new Date(filters.fechaDesde);
        return fechaLoan >= fechaDesde;
      });
    }
    if (filters.fechaHasta) {
      filtered = filtered.filter(loan => {
        const fechaLoan = new Date(loan.fecha_de_creacion.split('/').reverse().join('-'));
        const fechaHasta = new Date(filters.fechaHasta);
        return fechaLoan <= fechaHasta;
      });
    }

    setLoanDataFiltered(filtered);
  }

  const clearFilters = () => {
    setFilters({
      search: '',
      estado: '',
      sucursal: '',
      producto: '',
      montoMin: '',
      montoMax: '',
      fechaDesde: '',
      fechaHasta: ''
    });
    setLoanDataFiltered(loanData);
  }

  // Apply filters when filters change
  useEffect(() => {
    applyFilters();
  }, [filters, loanData]);

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        {/* Main content */}
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            overflow: 'auto',
          })}
        >
          <Stack
            spacing={2}
            sx={{
              alignItems: 'center',
              mx: 3,
              pb: 5,
              mt: { xs: 8, md: 0 },
            }}
          >
            <Header parent={"Gestión Comercial"} child={"Préstamos"} />
            <Container maxWidth={false} sx={{ px: 5, py: 5 }}>
              <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', fontFamily: 'Inter, "Noto Sans", sans-serif' }}>
                <Box maxWidth="lg" mx="auto" py={4}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" px={2} mb={2}>
                    <Typography variant="h4" fontWeight="bold">
                      Gestión de Préstamos
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <Button 
                        startIcon={<Assessment />} 
                        variant="outlined" 
                        sx={{ fontWeight: "bold" }} 
                        onClick={handleReportsMenuOpen}
                        endIcon={<ArrowDropDown />}
                      >
                        Reportes
                      </Button>
                    <Can modulo="prestamos" accion="crear">
                      <Button startIcon={<Add />} variant="contained" sx={{ fontWeight: "bold" }} color='primarybutton' onClick={()=>{navigate("/gestion_comercial/prestamos/crear")}}>
                        Nuevo Préstamo o Solicitud
                      </Button>
                    </Can>

                  
                    </Stack>
                  </Box>

                  {/* Tabs */}
                  <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2, mt:5 }} variant="scrollable" scrollButtons="auto" >
                    <Tab value={0} label="Préstamos Activos" sx={{fontSize:17, fontWeight:"bold"}}/>
                    <Tab value={1} label="Solicitudes" sx={{marginLeft:2, fontSize:17, fontWeight:"bold"}}/>
                    {localStorage.getItem('modulos').includes('iniciales') && <Tab value={2} label="Iniciales" sx={{marginLeft:2, fontSize:17, fontWeight:"bold"}}/>}
                    <Tab value={3} label="Irrecuperables" sx={{marginLeft:2, fontSize:17, fontWeight:"bold"}}/>
                    <Tab value={4} label="Garantias" sx={{marginLeft:2, fontSize:17, fontWeight:"bold"}}/>
                  </Tabs>

                  {/* Summary Cards */}


                  {tabValue === 0 ? 
                  !accessDenied ? 
                  <>
                   
                  <Grid container spacing={2} px={2}>
                    <Grid item xs={12} sm={4}>
                      <Card sx={{ backgroundColor: '#e8f5e8' }}>
                        <CardContent>
                          <Typography variant="subtitle1" color="success.main">Préstamos Activos</Typography>
                          <Typography variant="h5" fontWeight="bold" color="success.main">
                            {loanData.filter(loan => loan.estado_prestamo === "activo").length}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Card sx={{ backgroundColor: '#e3f2fd' }}>
                        <CardContent>
                          <Typography variant="subtitle1" color="primary.main">Monto Total Prestado</Typography>
                          <Typography variant="h5" fontWeight="bold" color="primary.main">
                            {formatNumber(loanData.reduce((acc, loan) => acc + parseFloat(loan.monto_aprobado), 0))}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                  <Box sx={{ mt:4}}>
                    {/* Filter Controls */}
                    <Stack direction="row" spacing={2} sx={{ mb: 3, alignItems: 'center' }}>
                      <Button 
                        variant="outlined" 
                        startIcon={<FilterList />}
                        onClick={() => setShowFilters(!showFilters)}
                        sx={{ fontWeight: "bold" }}
                      >
                        Filtros
                      </Button>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                        {loanDataFiltered.length} de {loanData.length} préstamos
                      </Typography>
                    </Stack>

                    {/* Filter Panel */}
                    <Collapse in={showFilters} sx={{ width: '100%', mb: 2 }}>
                      <Card sx={{ mb: 2 }}>
                        <CardContent>
                          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FilterList color="primary" />
                            Filtros de Búsqueda
                          </Typography>
                          
                          <Grid container spacing={2}>
                            {/* Estado Filter */}
                            <Grid item size={4}>
                              <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left' }}>
                                  Estado
                                </Typography>
                                <FormControl fullWidth>
                                  <Select
                                    value={filters.estado}
                                    onChange={(e) => handleFilterChange('estado', e.target.value)}
                                    sx={{backgroundColor: 'white'}}
                                    displayEmpty
                                  >
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value="activo">Activo</MenuItem>
                                    <MenuItem value="saldado">Saldado</MenuItem>
                                    <MenuItem value="cancelado">Cancelado</MenuItem>
                                  </Select>
                                </FormControl>
                              </Box>
                            </Grid>

                            {/* Sucursal Filter */}
                            <Grid item size={4}>
                              <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left' }}>
                                  Sucursal
                                </Typography>
                                <FormControl fullWidth>
                                  <Select
                                    value={filters.sucursal}
                                    onChange={(e) => handleFilterChange('sucursal', e.target.value)}
                                    sx={{backgroundColor: 'white'}}
                                    displayEmpty
                                  >
                                    <MenuItem value="">Todas</MenuItem>
                                    {sucursales.map((sucursal) => (
                                      <MenuItem key={sucursal} value={sucursal}>
                                        {sucursal}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Box>
                            </Grid>

                            {/* Producto Filter */}
                            <Grid item size={4}>
                              <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left' }}>
                                  Producto Financiero
                                </Typography>
                                <FormControl fullWidth>
                                  <Select
                                    value={filters.producto}
                                    onChange={(e) => handleFilterChange('producto', e.target.value)}
                                    sx={{backgroundColor: 'white'}}
                                    displayEmpty
                                  >
                                    <MenuItem value="">Todos</MenuItem>
                                    {productos.map((producto) => (
                                      <MenuItem key={producto} value={producto}>
                                        {producto}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Box>
                            </Grid>

                            {/* Monto Min */}
                            <Grid item size={3}>
                              <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left' }}>
                                  Monto Mínimo
                                </Typography>
                                <TextField
                                  fullWidth
                                  type="number"
                                  placeholder="0"
                                  value={filters.montoMin}
                                  onChange={(e) => handleFilterChange('montoMin', e.target.value)}
                                  sx={{backgroundColor: 'white'}}
                                  InputProps={{
                                    startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                                  }}
                                />
                              </Box>
                            </Grid>

                            {/* Monto Max */}
                            <Grid item size={3}>
                              <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left' }}>
                                  Monto Máximo
                                </Typography>
                                <TextField
                                  fullWidth
                                  type="number"
                                  placeholder="999999"
                                  value={filters.montoMax}
                                  onChange={(e) => handleFilterChange('montoMax', e.target.value)}
                                  sx={{backgroundColor: 'white'}}
                                  InputProps={{
                                    startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                                  }}
                                />
                              </Box>
                            </Grid>

                            {/* Fecha Desde */}
                            <Grid item size={3}>
                              <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left' }}>
                                  Fecha Desde
                                </Typography>
                                <TextField
                                  fullWidth
                                  type="date"
                                  value={filters.fechaDesde}
                                  onChange={(e) => handleFilterChange('fechaDesde', e.target.value)}
                                  sx={{backgroundColor: 'white'}}
                                />
                              </Box>
                            </Grid>

                            {/* Fecha Hasta */}
                            <Grid item size={3}>
                              <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold", textAlign: 'left' }}>
                                  Fecha Hasta
                                </Typography>
                                <TextField
                                  fullWidth
                                  type="date"
                                  value={filters.fechaHasta}
                                  onChange={(e) => handleFilterChange('fechaHasta', e.target.value)}
                                  sx={{backgroundColor: 'white'}}
                                />
                              </Box>
                            </Grid>
                          </Grid>

                          {/* Filter Actions */}
                          <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button
                              variant="outlined"
                              startIcon={<Clear />}
                              onClick={clearFilters}
                              sx={{ fontWeight: "bold" }}
                            >
                              Limpiar Filtros
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Collapse>
                    
                    {/* Quick Search Autocomplete */}
                    <Autocomplete
                      options={loanDataFiltered}
                      getOptionLabel={(option) => 
                        {
                          var value = "";
                          JSON.parse(option.producto_financiero_formulario)["campos"].filter(formulario => formulario.busqueda).map(formulario => {
                            if(JSON.parse(option.solicitud_campos)[formulario.id]!=null) {
                              value += " - "+JSON.parse(option.solicitud_campos)[formulario.id];
                            }
                          })
                          return (option.loan_id+" - "+option.cliente_nombre+" - "+option.cliente_identificacion+value)}}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Box display={"flex"} flexDirection={"row"} justifyContent={"left"} mt={2} ml={2}>
                          <Typography variant="body2" color="text.secondary">
                            {option.loan_id} 
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ml:5, fontWeight:"bold"}}>
                            Cliente:
                          </Typography>
      
                          <Typography variant="body2" color="text.secondary" sx={{ml:1}}>
                            {option.cliente_identificacion}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ml:1}}>
                              - {option.cliente_nombre}
                          </Typography>
                          
      
                          {JSON.parse(option.producto_financiero_formulario)["campos"].filter(formulario => formulario.busqueda).map(formulario => {
                            if(JSON.parse(option.solicitud_campos)[formulario.id]!=null) {
                            return (
                            <>
                            <Typography variant="body2" color="text.secondary" sx={{ml:5, fontWeight:"bold"}}>
                            {formulario.name}:
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ml:1}}>
                              {JSON.parse(option.solicitud_campos)[formulario.id]}
                            </Typography>
      
                            </>
                            );
                            }
                            return null;
                          })}
                          </Box>
                        </li>
                      )}
                      onChange={(e, val) => {handleInputChange(val)}}
                      renderInput={(params) => <TextField {...params} placeholder="Búsqueda rápida por préstamo específico" fullWidth />}
                      sx={{ mb: 3, bgcolor: "#f0f2f5", borderRadius: 2 }}
                      fullWidth
                      />
                  </Box>
                  {/* Show empty state if no loans */}
                  {loanDataFiltered.length === 0 ? (
                    <EmptyState 
                      icon={AccountBalance}
                      title="No hay préstamos activos"
                      description="Comienza creando tu primer préstamo o solicitud para gestionar tu cartera crediticia de manera eficiente."
                      features={[
                        {
                          icon: TrendingUp,
                          title: "Gestión Completa",
                          description: "Control total del ciclo de vida del préstamo"
                        },
                        {
                          icon: Schedule,
                          title: "Seguimiento Automático",
                          description: "Recordatorios y alertas de vencimientos"
                        },
                        {
                          icon: Assessment,
                          title: "Reportes Detallados",
                          description: "Análisis de rendimiento y cartera"
                        }
                      ]}
                      buttonText="Crear Nuevo Préstamo"
                      onButtonClick={puedeCrearPrestamo ? () => navigate('/gestion_comercial/prestamos/crear') : undefined}
                      helpText="¿Necesitas ayuda? Contacta a soporte técnico para guiarte en el proceso"
                    />
                  ) : (
                    <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none' }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <StyledCellHeader>ID Préstamo</StyledCellHeader>
                            <StyledCellHeader>Cliente</StyledCellHeader>
                            <StyledCellHeader>Producto Financiero</StyledCellHeader>
                            <StyledCellHeader>Monto Aprobado</StyledCellHeader>
                            <StyledCellHeader>Estado</StyledCellHeader>
                            <StyledCellHeader>Sucursal</StyledCellHeader>
                            <StyledCellHeader>Fecha de Creación</StyledCellHeader>
                            <StyledCellHeader>Acciones</StyledCellHeader>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {loanDataFiltered.map((loan) => (
                            <TableRow key={loan.id}>
                              <TableCell>{loan.loan_id}</TableCell>
                              <TableCell>{loan.cliente_nombre}</TableCell>
                              <TableCell>{loan.producto_financiero}</TableCell>
                              <TableCell>{formatNumber(loan.monto_aprobado)}</TableCell>
                              <TableCell>
                                <StatusChip status={capitalize(loan.estado_prestamo)} />
                              </TableCell>
                              <TableCell>{loan.sucursal_nombre}</TableCell>
                              <TableCell>{loan.fecha_de_creacion}</TableCell>
                              <TableCell>
                                <IconButton size='small' onClick={()=>{navigate(`/gestion_comercial/prestamos/detalle/${loan.id}`)}}>
                                  <RemoveRedEyeOutlined />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  </>
                  :
                  <AccessDeniedBox
                    title={'Acceso denegado'}
                    message={'No tienes permisos para ver esta sección de Préstamos. Contacta a tu administrador para solicitar acceso o verifica tus roles y permisos.'}
                  />
                  :<></>}

                  {tabValue === 1 && <Solicitudes onLoanCreated={fetchLoans} />}
                  {tabValue === 2 && <Iniciales/>}
                  {tabValue === 3 && <IrrecuperablesLoans/>}
                  {tabValue === 4 && <Garantias/>}
                </Box>
              </Box>
            </Container>
          </Stack>
        </Box>
      </Box>

      {/* Reports Menu */}
      <Menu
        anchorEl={reportsMenuAnchor}
        open={Boolean(reportsMenuAnchor)}
        onClose={handleReportsMenuClose}
        PaperProps={{
          sx: {
            minWidth: 280,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            mt: 1
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => handleReportSelect('balancedeprestamos')}>
          <ListItemIcon>
            <TableChart color="primary" />
          </ListItemIcon>
          <ListItemText 
            primary="Balance de Préstamos" 
            secondary="Balance general de todos los préstamos"
          />
        </MenuItem>
        
        <MenuItem onClick={() => handleReportSelect('interesescobradosperiodo')}>
          <ListItemIcon>
            <Schedule color="primary" />
          </ListItemIcon>
          <ListItemText 
            primary="Intereses Pagados por Periodos" 
            secondary="Registro detallado de intereses pagados por periodos"
          />
        </MenuItem>

        <MenuItem onClick={() => handleReportSelect('cuadredecajaprestamos')}>
          <ListItemIcon>
            <AccountBalanceWallet color="primary" />
          </ListItemIcon>
          <ListItemText 
            primary="Cuadre de Caja de Préstamos" 
            secondary="Registro detallado de cuadre de caja de préstamos"
          />
        </MenuItem>
        
        <MenuItem onClick={() => handleReportSelect('antiguedadsaldo')}>
          <ListItemIcon>
            <Warning color="error" />
          </ListItemIcon>
          <ListItemText
            primary="Antiguedad de Saldo"
            secondary="Lista de préstamos en mora"
          />
        </MenuItem>

        <MenuItem onClick={() => handleReportSelect('historialproducto')}>
          <ListItemIcon>
            <Inventory2Outlined color="primary" />
          </ListItemIcon>
          <ListItemText
            primary="Historial de Producto"
            secondary="Detalle de cobros, pagos y recuperaciones por producto financiado"
          />
        </MenuItem>

        <Divider />
        
      
       
      </Menu>
    </AppTheme>
  );
};

export default LoansManagement;
